import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import gameService from "../services/gameService";
import categoryService from "../services/categoryService";
import providerService from "../services/providerService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./HomePage.css";

const HomePage = () => {
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showIframe, setShowIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");
  const [iframeLoading, setIframeLoading] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterProvider, setFilterProvider] = useState("");
  const [filterReleaseDateFrom, setFilterReleaseDateFrom] = useState("");
  const [filterReleaseDateTo, setFilterReleaseDateTo] = useState("");
  const iframeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!iframeRef.current || !iframeUrl || !showIframe) return;

    setIframeLoading(true);

    let iframeLoaded = false;
    let fallbackTriggered = false;

    const maybeSetLoaded = () => {
      if (iframeLoaded && fallbackTriggered) {
        setIframeLoading(false);
      }
    };

    const checkIframeContent = () => {
      try {
        const iframeDocument =
          iframeRef.current.contentDocument ||
          iframeRef.current.contentWindow.document;
        const bodyText = iframeDocument.body?.innerText || "";
        const containsText = bodyText.includes("TurnkeyXGaming");
        const loaderContainer = iframeDocument.querySelector(".loader-container1");

        if (containsText || !loaderContainer) {
          clearInterval(interval);
        }
      } catch (error) {
        console.warn("Cross-origin restriction:", error);
      }
    };

    const handleIframeLoad = () => {
      iframeLoaded = true;
      maybeSetLoaded();
      clearInterval(interval);
    };

    iframeRef.current.addEventListener("load", handleIframeLoad);

    const interval = setInterval(checkIframeContent, 1000);

    const fallbackTimer = setTimeout(() => {
      fallbackTriggered = true;
      maybeSetLoaded();
      clearInterval(interval);
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(fallbackTimer);
    };
  }, [iframeUrl, showIframe]);

  const handlePlayGame = async (gameId) => {
    setIframeLoading(true);
    setShowIframe(true);
    try {
      const response = await gameService.playGameById(gameId);
      const gameLink = response.data?.data?.game_link || response.data?.data;
      if (gameLink) {
        setIframeUrl(gameLink);
      } else {
        toast.error("গেম লিঙ্ক পাওয়া যায়নি!");
        setShowIframe(false);
        setIframeLoading(false);
      }
    } catch (error) {
      toast.error("গেম লোড করতে সমস্যা: " + (error.response?.data?.error || error.message));
      setShowIframe(false);
    }
  };

  useEffect(() => {
    const apiKey = localStorage.getItem("apiKey");
    if (!apiKey) {
      toast.error("দয়া করে API কী সেট করুন");
      navigate("/login");
      return;
    }
    loadCategories();
    loadProviders();
    loadGames();
  }, [navigate]);

  useEffect(() => {
    if (filterCategory) {
      const filtered = providers.filter(
        (prov) => prov.parentcategory?._id === filterCategory
      );
      setFilteredProviders(filtered);
      setFilterProvider("");
    } else {
      setFilteredProviders(providers);
    }
  }, [filterCategory, providers]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && showIframe) {
        setShowIframe(false);
        setIframeUrl("");
      }
    };
    window.addEventListener("keydown", handleEscapeKey);
    return () => window.removeEventListener("keydown", handleEscapeKey);
  }, [showIframe]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories();
      setCategories(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      toast.error("ক্যাটাগরি লোড করতে সমস্যা: " + (error.response?.data?.error || error.message));
    }
  };

  const loadProviders = async () => {
    try {
      const response = await providerService.getProviders();
      setProviders(Array.isArray(response.data.data) ? response.data.data : []);
      setFilteredProviders(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      toast.error("প্রোভাইডার লোড করতে সমস্যা: " + (error.response?.data?.error || error.message));
    }
  };

  const loadGames = async (append = false, filters = {}) => {
    setLoading(true);
    try {
      const response = await gameService.getGamesByPagination({
        ...filters,
        page,
        limit: 50,
      });
      const newGames = Array.isArray(response.data.data) ? response.data.data : [];
      setGames((prevGames) => (append ? [...prevGames, ...newGames] : newGames));
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      toast.error("গেম লোড করতে সমস্যা: " + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    const filters = {
      name: filterName,
      category: filterCategory,
      provider: filterProvider,
      releaseDateFrom: filterReleaseDateFrom,
      releaseDateTo: filterReleaseDateTo,
    };
    loadGames(false, filters);
  };

  const resetFilters = () => {
    setFilterName("");
    setFilterCategory("");
    setFilterProvider("");
    setFilterReleaseDateFrom("");
    setFilterReleaseDateTo("");
    setPage(1);
    loadGames(false);
  };

  const handleLoadMore = () => {
    if (page < totalPages) {
      setPage((prevPage) => prevPage + 1);
      const filters = {
        name: filterName,
        category: filterCategory,
        provider: filterProvider,
        releaseDateFrom: filterReleaseDateFrom,
        releaseDateTo: filterReleaseDateTo,
      };
      loadGames(true, filters);
    }
  };

  return (
    <div className="game-dashboard">
      <Header />
      <ToastContainer />
      <div className="container py-5">
        <h1 className="main-title">গেম তালিকা</h1>

        {/* Filter Form */}
        <div className="filter-form mb-4">
          <h4 className="filter-title">গেম ফিল্টার</h4>
          <form onSubmit={handleFilterSubmit}>
            <div className="row g-3 align-items-center">
              <div className="col-md-3 col-12">
                <select
                  className="form-control"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  disabled={loading}
                >
                  <option value="">সব ক্যাটাগরি</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3 col-12">
                <select
                  className="form-control"
                  value={filterProvider}
                  onChange={(e) => setFilterProvider(e.target.value)}
                  disabled={loading}
                >
                  <option value="">সব প্রোভাইডার</option>
                  {filteredProviders.map((prov) => (
                    <option key={prov._id} value={prov._id}>
                      {prov.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 col-12 d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-register rounded-pill flex-fill"
                  disabled={loading}
                >
                  ফিল্টার
                </button>
                <button
                  type="button"
                  className="btn btn-cancel rounded-pill flex-fill"
                  onClick={resetFilters}
                  disabled={loading}
                >
                  রিসেট
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Loading & No Games */}
        {loading && (
          <div className="loader">
            <div className="spinner"></div>
            <h3><b>Oracle Technology LLC</b></h3>
            <p><a href="https://www.oracleapi.net" target="_blank" rel="noopener noreferrer">www.oracleapi.net</a></p>
            <p>ওরাকল গেমস চালু হচ্ছে...</p>
          </div>
        )}

        {!loading && games.length === 0 && (
          <div className="alert alert-warning">কোনো গেম পাওয়া যায়নি।</div>
        )}

        {/* Game List */}
        {!loading && games.length > 0 && (
          <div className="game-container">
            {games.map((game) => (
              <div key={game._id} className="game-card">
                <img
                  src={`${process.env.REACT_APP_API_URL}/${game.image}`}
                  alt={game.name}
                />
                <h3>{game.name}</h3>
                <p>ক্যাটাগরি: {game.category?.name || "N/A"}</p>
                <p>প্রোভাইডার: {game.provider?.name || "N/A"}</p>
                <button
                  className="play-button"
                  onClick={() => handlePlayGame(game.game_uuid)}
                  disabled={iframeLoading}
                >
                  {iframeLoading ? "লোড হচ্ছে..." : "এখন খেলুন"}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Iframe Modal */}
        {showIframe && (
          <div className="iframe-modal" onClick={() => { setShowIframe(false); setIframeUrl(""); }}>
            <div className="iframe-container" onClick={(e) => e.stopPropagation()}>
              <button className="close-button" onClick={() => { setShowIframe(false); setIframeUrl(""); }}>
                ✕
              </button>
              {iframeLoading && (
                <div className="iframe-loader">
                  <div className="spinner"></div>
                  <h3><b>Oracle Technology LLC</b></h3>
                  <p><a href="https://www.oracleapi.net" target="_blank" rel="noopener noreferrer">www.oracleapi.net</a></p>
                  <p>ওরাকল গেমস চালু হচ্ছে...</p>
                </div>
              )}
              <iframe
                ref={iframeRef}
                src={iframeUrl}
                title="Game"
                width="100%"
                height="100%"
                style={{ border: "none", borderRadius: "20px", display: iframeLoading ? "none" : "block" }}
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Load More */}
        {!loading && page < totalPages && (
          <div className="d-flex justify-content-center mt-4">
            <button className="btn btn-register rounded-pill px-4" onClick={handleLoadMore}>
              আরও লোড করুন
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;